<template>
  <div v-show="visible" class="modal-overlay" @click.self="close">
    <div class="modal-dialog picker-dialog">
      <div class="modal-header">
        <h4><i class="fas" :class="modeIcon"></i> {{ title }}</h4>
        <button @click="close" class="modal-close">&times;</button>
      </div>
      <div class="modal-body">
        <!-- 表单字段模式：双面板（主表字段列表 + 手动输入） -->
        <template v-if="mode === 'field'">
          <div class="field-mode-wrap">
            <div class="field-section">
              <div class="field-section-title"><i class="fas fa-table"></i> 主表字段</div>
              <div class="field-list">
                <div v-for="col in fieldList" :key="col.name"
                  :class="['field-item', { selected: selectedField === col.name }]"
                  @click="onSelectField(col)">
                  <span class="field-item-name">{{ col.description || col.name }}</span>
                  <span class="field-item-code">{{ col.name }}</span>
                </div>
                <div v-if="fieldList.length === 0 && fieldLoading" class="empty-tip">加载中...</div>
                <div v-if="fieldList.length === 0 && !fieldLoading" class="empty-tip">暂无主表字段<br/><small>请先在流程设计中绑定表单</small></div>
              </div>
            </div>
            <div class="field-section">
              <div class="field-section-title"><i class="fas fa-keyboard"></i> 手动输入</div>
              <div class="field-manual">
                <input v-model="manualFieldCode" class="form-control" placeholder="输入字段编号（如 CustomField1）" @keydown.enter="confirmFieldManual" />
                <button @click="confirmFieldManual" class="btn btn-primary btn-xs" style="margin-top:8px;width:100%">
                  <i class="fas fa-check"></i> 确定
                </button>
              </div>
            </div>
          </div>
        </template>
        <!-- 部门/角色/用户模式 -->
        <template v-else>
          <div class="search-bar">
            <input v-model="searchText" class="form-control" :placeholder="searchPlaceholder" @input="onSearch">
            <span class="search-icon"><i class="fas fa-search"></i></span>
          </div>
          <div class="picker-list">
            <!-- 部门/角色：树形（扁平化渲染） -->
            <template v-if="mode !== 'user'">
              <div v-for="node in flattenedTree" :key="node.id"
                :class="['tree-item', { selected: selected?.id === node.id }]"
                :style="{ paddingLeft: (node.depth * 20 + 12) + 'px' }"
                @click="onSelect(node)">
                <span v-if="node.children && node.children.length" class="tree-expand"
                  :class="{ expanded: expandedIds.includes(node.id) }"
                  @click.stop="toggleExpand(node.id)">
                  <i class="fas fa-caret-right"></i>
                </span>
                <span v-else class="tree-expand-placeholder"></span>
                <i class="fas" :class="node.children && node.children.length ? 'fa-folder' : 'fa-file'"></i>
                <span class="tree-label">{{ node.name }}</span>
              </div>
              <div v-if="flattenedTree.length === 0 && !loading" class="empty-tip">无匹配项</div>
              <div v-if="flattenedTree.length === 0 && loading" class="empty-tip">加载中...</div>
            </template>
            <!-- 人员：平铺列表 -->
            <template v-else>
              <div v-for="u in filteredUsers" :key="u.id"
                :class="['picker-item', { selected: selected?.id === u.id }]"
                @click="onSelectUser(u)">
                <span class="picker-item-name">{{ u.name }}</span>
                <span class="picker-item-extra">{{ u.account }}</span>
              </div>
              <div v-if="filteredUsers.length === 0" class="empty-tip">{{ loading ? '加载中...' : '无匹配项' }}</div>
            </template>
          </div>
        </template>
      </div>
      <div class="modal-footer">
        <button @click="close" class="btn btn-default btn-sm">取消</button>
        <button @click="confirmSelect" class="btn btn-primary btn-sm" :disabled="!canConfirm">
          <i class="fas fa-check"></i> 确定
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getOrgTree } from '../api/org'
import { getRoleTree } from '../api/role'
import { getUserList } from '../api/user'
import { getTableColumns } from '../api/system'

const props = defineProps({
  visible: Boolean,
  mode: { type: String, default: 'dept' },
  title: { type: String, default: '' },
  flowGuid: { type: String, default: '' },
  tableName: { type: String, default: '' }
})

const emit = defineEmits(['close', 'pick'])

const searchText = ref('')
const treeData = ref([])
const userList = ref([])
const loading = ref(false)
const selected = ref(null)
const expandedIds = ref([])

// 表单字段模式
const fieldList = ref([])
const fieldLoading = ref(false)
const selectedField = ref('')
const manualFieldCode = ref('')

let searchTimer = null

const modeIcon = computed(() => {
  if (props.mode === 'dept') return 'fa-building'
  if (props.mode === 'role') return 'fa-users'
  if (props.mode === 'field') return 'fa-table'
  return 'fa-user'
})
const searchPlaceholder = computed(() => {
  if (props.mode === 'dept') return '搜索组织名称...'
  if (props.mode === 'role') return '搜索角色名称...'
  return '搜索人员姓名/账号...'
})
const canConfirm = computed(() => {
  if (props.mode === 'field') return !!selectedField
  return !!selected.value
})

// 扁平化树：递归获取当前可见节点列表
const flattenedTree = computed(() => {
  const s = searchText.value.toLowerCase()
  const source = s ? filterTree(treeData.value, s) : treeData.value
  const result = []
  flatten(source, 0, result)
  return result
})

function flatten(nodes, depth, result) {
  for (const node of nodes) {
    result.push(node)
    if (node.children && node.children.length && expandedIds.value.includes(node.id)) {
      flatten(node.children, depth + 1, result)
    }
  }
}

function filterTree(nodes, s) {
  const result = []
  for (const node of nodes) {
    const match = node.name.toLowerCase().includes(s)
    const children = node.children ? filterTree(node.children, s) : []
    if (match || children.length > 0) {
      result.push({ ...node, children, depth: node.depth ?? 0 })
    }
  }
  return result
}

const filteredUsers = computed(() => {
  if (!searchText.value) return userList.value
  const s = searchText.value.toLowerCase()
  return userList.value.filter(u =>
    (u.name || '').toLowerCase().includes(s) || (u.account || '').toLowerCase().includes(s)
  )
})

watch(() => [props.visible, props.mode], ([v, m]) => {
  if (!v) return
  searchText.value = ''
  selected.value = null
  treeData.value = []
  userList.value = []
  expandedIds.value = []
  selectedField.value = ''
  manualFieldCode.value = ''
  fieldList.value = []
  if (m === 'dept') loadDeptTree()
  else if (m === 'role') loadRoleTree()
  else if (m === 'field') loadFieldList()
  else loadUsers()
}, { immediate: false })

/** 将已嵌套的树节点递归转换为组件统一格式 */
function transformTreeNodes(nodes, depth = 0) {
  if (!Array.isArray(nodes)) return []
  return nodes.map(node => ({
    id: node.guid || node.Guid || node.id || '',
    name: node.ousName || node.OUsName || node.roleName || node.RoleName || node.name || node.Name || '',
    depth,
    children: transformTreeNodes(node.children || node.Children || [], depth + 1)
  }))
}

async function loadDeptTree() {
  loading.value = true
  try {
    const res = await getOrgTree()
    // res 已为 { resultType, data: [...] }，data 已是嵌套树 [{ guid, ousName, children: [...] }, ...]
    const list = res?.data ?? []
    treeData.value = Array.isArray(list) ? transformTreeNodes(list) : []
    expandedIds.value = treeData.value.map(n => n.id)
  } catch (e) { console.error('Load dept tree error:', e) }
  loading.value = false
}

async function loadRoleTree() {
  loading.value = true
  try {
    const res = await getRoleTree()
    // res.data 已是嵌套树 [{ guid, roleName, children: [...] }, ...]
    const list = res?.data ?? []
    treeData.value = Array.isArray(list) ? transformTreeNodes(list) : []
    expandedIds.value = treeData.value.map(n => n.id)
  } catch (e) { console.error('Load role tree error:', e) }
  loading.value = false
}

async function loadUsers() {
  loading.value = true
  try {
    const res = await getUserList({ pageSize: 500, searchText: searchText.value || '' })
    const list = res?.data ?? res?.Data ?? res ?? []
    const rows = Array.isArray(list) ? list : (list?.rows ?? list?.Rows ?? [])
    userList.value = rows.map(u => ({
      id: u.guid || u.Guid,
      name: u.displayName || u.DisplayName || u.name || u.Name || u.account || u.Account || '',
      account: u.account || u.Account || ''
    }))
  } catch (e) { console.error('Load users error:', e) }
  loading.value = false
}

/** 加载主表字段列表 */
async function loadFieldList() {
  fieldLoading.value = true
  try {
    const tableName = props.tableName
    if (!tableName) { fieldList.value = []; fieldLoading.value = false; return }
    const res = await getTableColumns(tableName)
    const cols = res?.data ?? res?.Data ?? res ?? []
    fieldList.value = Array.isArray(cols) ? cols : []
  } catch (e) { console.error('Load field list error:', e); fieldList.value = [] }
  fieldLoading.value = false
}

function onSearch() {
  clearTimeout(searchTimer)
  if (props.mode === 'user') {
    searchTimer = setTimeout(loadUsers, 300)
  }
}

function toggleExpand(id) {
  const i = expandedIds.value.indexOf(id)
  i >= 0 ? expandedIds.value.splice(i, 1) : expandedIds.value.push(id)
}

function onSelect(node) { selected.value = node }
function onSelectUser(u) { selected.value = u }

function onSelectField(col) {
  selectedField.value = col.name
}

function confirmFieldManual() {
  if (manualFieldCode.value.trim()) {
    selectedField.value = manualFieldCode.value.trim()
    emit('pick', { id: selectedField.value, name: selectedField.value, account: selectedField.value })
    close()
  }
}

function confirmSelect() {
  if (props.mode === 'field') {
    if (selectedField.value) {
      emit('pick', { id: selectedField.value, name: selectedField.value, account: selectedField.value })
      close()
    }
    return
  }
  if (selected.value) {
    emit('pick', { id: selected.value.id, name: selected.value.name, account: selected.value.account || '' })
    close()
  }
}

function close() { emit('close') }
</script>

<style scoped>
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 2000; }
.modal-dialog { background: #fff; border-radius: 10px; box-shadow: 0 8px 30px rgba(0,0,0,0.15); min-width: 460px; max-width: 560px; display: flex; flex-direction: column; }
.picker-dialog { width: 500px; max-height: 72vh; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #e5e7eb; }
.modal-header h4 { margin: 0; font-size: 16px; font-weight: 600; color: #1f2937; display: flex; align-items: center; gap: 8px; }
.modal-close { border: none; background: none; font-size: 22px; color: #9ca3af; cursor: pointer; padding: 0 4px; line-height: 1; }
.modal-close:hover { color: #374151; }
.modal-body { padding: 16px 20px; overflow-y: auto; max-height: 50vh; }
.modal-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 20px; border-top: 1px solid #e5e7eb; }
.btn { padding: 8px 18px; border-radius: 6px; font-size: 14px; cursor: pointer; border: 1px solid #d1d5db; background: #fff; color: #374151; font-weight: 500; }
.btn:hover { background: #f3f4f6; }
.btn-default { border: 1px solid #d1d5db; background: #fff; }
.btn-primary { border: 1px solid #10b981; background: #10b981; color: #fff; }
.btn-primary:hover { background: #059669; }
.btn-primary:disabled { background: #a7f3d0; border-color: #a7f3d0; cursor: not-allowed; }
.btn-xs { padding: 6px 12px; font-size: 13px; }
.search-bar { position: relative; margin-bottom: 10px; }
.form-control {
  width: 100%; padding: 9px 12px 9px 32px; border: 1px solid #d1d5db; border-radius: 6px;
  font-size: 14px; outline: none; box-sizing: border-box; color: #1f2937;
}
.form-control:focus { border-color: #10b981; box-shadow: 0 0 0 2px rgba(16,185,129,0.12); }
.form-control::placeholder { color: #9ca3af; }
.search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9ca3af; font-size: 14px; }
.picker-list { max-height: 380px; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 6px; padding: 4px 0; }

.tree-item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; cursor: pointer; font-size: 14px; line-height: 1.5;
  transition: background 0.12s; user-select: none; border-radius: 2px;
}
.tree-item:hover { background: #f3f4f6; }
.tree-item.selected { background: #ecfdf5; color: #065f46; font-weight: 500; }
.tree-expand { width: 16px; flex-shrink: 0; text-align: center; cursor: pointer; color: #6b7280; font-size: 12px; transition: transform 0.2s; }
.tree-expand.expanded { transform: rotate(90deg); }
.tree-expand-placeholder { width: 16px; flex-shrink: 0; }
.tree-item i.fa-folder, .tree-item i.fa-file { color: #6b7280; font-size: 14px; width: 16px; flex-shrink: 0; }
.tree-item.selected i.fa-folder, .tree-item.selected i.fa-file { color: #059669; }
.tree-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.picker-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; cursor: pointer; font-size: 14px;
  transition: background 0.12s; border-bottom: 1px solid #f3f4f6;
}
.picker-item:last-child { border-bottom: none; }
.picker-item:hover { background: #f3f4f6; }
.picker-item.selected { background: #ecfdf5; color: #065f46; font-weight: 500; }
.picker-item-name { font-weight: 500; color: #1f2937; }
.picker-item.selected .picker-item-name { color: #065f46; }
.picker-item-extra { font-size: 13px; color: #6b7280; }

/* 表单字段模式 */
.field-mode-wrap { display: flex; gap: 16px; min-height: 300px; }
.field-section { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.field-section-title { font-size: 14px; font-weight: 600; color: #374151; padding: 6px 0 10px; border-bottom: 2px solid #10b981; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.field-list { flex: 1; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 6px; padding: 4px 0; max-height: 280px; }
.field-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 9px 12px; cursor: pointer; font-size: 13px;
  transition: background 0.12s; border-bottom: 1px solid #f9fafb;
}
.field-item:last-child { border-bottom: none; }
.field-item:hover { background: #f3f4f6; }
.field-item.selected { background: #ecfdf5; }
.field-item-name { color: #1f2937; font-weight: 500; }
.field-item.selected .field-item-name { color: #065f46; }
.field-item-code { font-size: 12px; color: #6b7280; font-family: 'Consolas', 'Courier New', monospace; background: #f3f4f6; padding: 1px 6px; border-radius: 3px; }
.field-item.selected .field-item-code { background: #d1fae5; color: #065f46; }
.field-manual { padding: 4px 0; }
.field-manual .form-control { padding-left: 12px; }

.empty-tip { text-align: center; padding: 40px 20px; color: #9ca3af; font-size: 14px; line-height: 1.6; }
</style>
